import React, { useMemo } from "react";

import {
  Typography,
  TableMetadata,
  Label,
  TextField,
} from "@king-ict/eupisi-ui/components";
import { TableProps, Table } from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useBusinessRule } from "../../hooks";
import { EnrollmentSearchItem, EnrollmentStatusEnum } from "../../models";
import style from "./EnrollmentList.module.scss";

export interface EnrollmentListProps {
  tableProps: TableProps;
  renderLocationsAndProgramsColumn: (
    enrollmentRequest: EnrollmentSearchItem
  ) => React.ReactNode;
  renderCheckBox?: (enrollmentRequest: EnrollmentSearchItem) => React.ReactNode;
}

export const EnrollmentList = ({
  tableProps,
  renderLocationsAndProgramsColumn,
  renderCheckBox,
}: EnrollmentListProps) => {
  const { t } = useTranslation();
  const { canEvaluateEnrollment } = useBusinessRule();
  const getEnrollmentStatusColor = (statusId: number) => {
    switch (statusId) {
      case EnrollmentStatusEnum.Rejected:
        return "danger";
      case EnrollmentStatusEnum.Confirmed:
        return "success";
      case EnrollmentStatusEnum.InProgress:
        return "warning";
      default:
        return "success";
    }
  };

  const enrollmentTableMetadata = useMemo(
    () =>
      ({
        rowKey: "id",
        rowToolTip: () => {},
        columns: [
          {
            columnKey: "id",
            cellClassName: "padding0",
            header: "",
            accessor: (enrollmentRequest) => {
              return (
                <React.Fragment key={`row1_${enrollmentRequest.id}`}>
                  <Row
                    key={`row_${enrollmentRequest.id}`}
                        className={`${style.margin0} ${style.columnPadding} 
                        ${style.columnMargin} ${style.approvedBackground} 
                        ${enrollmentRequest.suggestedPedagogicalYearInstitutionProgramDetailId
                            ? style.suggestBorder
                            : ""
                    }`}
                  >        
                   { canEvaluateEnrollment() ? (  
                    <Col xs={1} className={`${style.padding0}`}>
                      {renderCheckBox && renderCheckBox(enrollmentRequest)}
                    </Col>
                   ) : null } 
                    <Col xs={7} className={style.padding0}>
                      <Row className={style.margin0}>
                        <Col xs={5} className={style.padding0}>
                          <Typography variant="baseBold">
                            {`${enrollmentRequest.sort}. 
                            ${enrollmentRequest.firstName} 
                            ${enrollmentRequest.lastName}`}
                          </Typography>
                        </Col>
                        <Col xs={5}>
                          <Label
                            color={getEnrollmentStatusColor(
                              enrollmentRequest.status
                            )}
                          >
                            {enrollmentRequest.statusName}
                          </Label>
                        </Col>
                        <Col xs={2}></Col>
                      </Row>
                      <Row className={style.margin0}>
                        <Col xs={5} className={style.padding0}>
                          <TextField
                            type="text"
                            label={t("person.dateOfBirth")}
                            value={hrDate(enrollmentRequest.dateOfBirth)}
                            plaintext
                          />
                        </Col>
                        <Col xs={5}>
                          <TextField
                            type="text"
                            label={t("person.permanentResidence")}
                            value={enrollmentRequest.permenentResidence}
                            plaintext
                          />
                        </Col>
                        <Col xs={2}>
                          <TextField
                            type="text"
                            label={t("criterion.points")}
                            value={enrollmentRequest.points}
                            plaintext
                            color="success"
                          />
                        </Col>
                      </Row>
                      <Row className={style.margin0}>
                        <Col xs={5} className={style.padding0}>
                          <TextField
                            type="text"
                            label={t("person.oib")}
                            value={enrollmentRequest.oib}
                            plaintext
                          />
                        </Col>
                        <Col xs={5}>
                          <TextField
                            type="text"
                            label={t("person.temporaryResidence")}
                            value={enrollmentRequest.temporaryResidence}
                            plaintext
                          />
                        </Col>
                        {(enrollmentRequest.isDirectEnrollment || enrollmentRequest.priority > 1 || enrollmentRequest.confirmedEnrollmentInAnotherInstitution) &&
                          <Row className={style.margin0}>
                            <Col xs={12} className={style.padding0}>
                            <Typography variant="baseBold">
                            {enrollmentRequest.isDirectEnrollment
                              ? `${t("criterion.directEnrollment")}; `
                              : null
                            }

                            {enrollmentRequest.priority > 1
                              ? `${t("enrollmentList.notFirstPriorityRequest")}; `
                              : null
                            }

                            {enrollmentRequest.confirmedEnrollmentInAnotherInstitution
                              ? `${t("enrollmentList.enrolledIn")} ${enrollmentRequest.confirmedEnrollmentInAnotherInstitution.institutionName}, ${enrollmentRequest.confirmedEnrollmentInAnotherInstitution.townName}; `
                              : null
                            }
                            </Typography>
                            </Col>
                          </Row>
                        }
                      </Row>
                    </Col>
                    <Col
                      xs={4}
                      className={`${style.preferencesAndLocations} ${style.padding0}`}
                    >
                      {renderLocationsAndProgramsColumn &&
                        renderLocationsAndProgramsColumn(enrollmentRequest)}
                    </Col>                    
                  </Row>
                </React.Fragment>
              );
            },
          },
        ],
      } as TableMetadata<EnrollmentSearchItem>),
    [t, renderLocationsAndProgramsColumn, renderCheckBox]
  );

  const resolveTableProps = () => {
    return { ...tableProps, metadata: enrollmentTableMetadata } as TableProps;
  };
  return (
    <>
      <Row className={style.margin0}>
        <Col xs={12} className={style.padding0}>
          <Table {...resolveTableProps()}></Table>
        </Col>
      </Row>
    </>
  );
};
